/**
 * Générateur — Attendu 952 — Connaitre les propriétés du demi-tour.
 * Niveau : 5ème | Domaine : Espace et géométrie / Transformations
 *
 * Plan de cartes (version alignée sur la spécification 952 du 2026-09-21) :
 *
 *   Carte « Déduire par les propriétés du demi-tour » — le dispositif central :
 *     la figure de DÉPART porte toutes les cotes, son IMAGE n'en porte aucune,
 *     et la question porte sur l'image. Mesurer devient impraticable ; il ne
 *     reste qu'à énoncer la propriété et à l'appliquer.
 *   Carte « Conclure par une propriété » — alignement, image d'une droite,
 *     centre invariant et caractère involutif : on ne transporte plus une
 *     mesure, on conclut, et l'on désigne la propriété invoquée.
 *   Carte « Définition, propriété, ou ni l'un ni l'autre ? » — le statut de ce
 *     qu'on affirme, que le préambule du programme demande d'expliciter.
 *   Carte « Les propriétés du demi-tour (Vrai/Faux) » — le mélange des sept.
 *
 * ── Spécification 952 ──────────────────────────────────────────────────────
 *
 * Bornes appliquées :
 *   liste_des_proprietes_retenues : les SEPT propriétés de PROPRIETES, et
 *     aucune autre (exclusion 7) ; chacune porte son statut, admise ou démontrée
 *   longueur_des_cotes_de_la_figure 3 à 8 cm, à une décimale
 *   mesure_des_angles_de_la_figure 25 à 130 degrés, entiers
 *   orientation_de_la_figure 0 à 360 degrés
 *   distance_minimale_du_centre_aux_sommets 2, et centre JAMAIS dans la figure
 *   aire_minimale_du_triangle 8
 *   ecart_a_l_alignement_dans_les_cas_non_alignes 3 à 6 degrés
 *   proportion_de_cas_ou_la_propriete_conclut 0,5 à 0,75 → 2 sur 3
 *   nombre_de_proprietes_mobilisees_par_question 1 ou 2
 *   precision_des_longueurs_attendues 0,1 cm
 *
 * Exclusions appliquées : aucune cote sur la figure image ; centre hors de la
 * figure et à deux unités au moins de chaque sommet ; aucune rotation d'un
 * autre angle sous le nom de symétrie centrale ; figure et image jamais
 * superposées (le centre hors d'un convexe le garantit) ; aucun triangle
 * d'aire inférieure à 8 ni à sommets confondus ; les noms de l'image ne se
 * déduisent jamais alphabétiquement de ceux du départ ; aucune question sur une
 * propriété hors liste.
 *
 * Erreurs visées → mots-clés :
 *   Mesurer sur le dessin au lieu d'invoquer la conservation → mesure_sur_le_dessin
 *   Ne connaître que la conservation des longueurs           → seulement_les_longueurs
 *   Transporter la mesure du mauvais côté                    → mauvaise_correspondance
 *   Croire que le demi-tour change les mesures               → demi_tour_change_les_mesures
 *   Attribuer la conservation à une transformation particulière → conservation_mal_attribuee
 *   Confondre « image d'une droite parallèle » et alignement → parallele_pour_alignement
 *   Conclure dans le sens réciproque                         → reciproque_mal_utilisee
 *   Répondre sans unité, ou en cm là où des degrés sont attendus → unite_manquante
 *
 * Graduation → cartes :
 *   niveau 1 (longueur)          proprietes_texte, proprietes_qcm_unique
 *   niveau 2 (angle)             proprietes_texte, proprietes_qcm_unique
 *   niveau 3 (longueur + angle)  proprietes_double_texte_trou_saisie
 *   niveau 4 (alignement)        conclure_alignement_qcm_unique
 *   niveau 5 (droite parallèle)  conclure_parallele_qcm_unique
 *   niveau 6 (aires)             proprietes_texte, proprietes_qcm_unique
 *   niveau 7 (centre, réciproque) conclure_centre_qcm_unique
 *   niveau 8 (mélange)           vrai_faux_tableau_vf, statut_qcm_unique
 *   réglage : avec ou sans les noms des sommets de l'image
 *
 * VIGILANCE 2 : la phrase qui énonce la propriété est TOUJOURS construite
 * depuis la propriété réellement mobilisée (table PROPRIETES), jamais écrite en
 * dur dans un énoncé. VIGILANCE 3 : un cas non aligné ne conclut JAMAIS par la
 * conservation de l'alignement, mais par le caractère involutif du demi-tour.
 */

import {
    randInt, randomChoice, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 952;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PROP = 'Déduire par les propriétés du demi-tour';
const L_CONCLURE = 'Conclure par une propriété';
const L_STATUT = 'Définition, propriété, ou ni l’un ni l’autre ?';
const L_VF = 'Les propriétés du demi-tour (Vrai/Faux)';

// ═══════════════════════════════════════════════════════════
// LES SEPT PROPRIÉTÉS RETENUES — et aucune autre (exclusion 7)
// Chacune porte son STATUT, admise ou démontrée (préambule du programme).
// ═══════════════════════════════════════════════════════════

const PROPRIETES = [
    { id: 'longueurs', enonce: 'Le demi-tour conserve les longueurs.', statut: 'admise', commune: true },
    { id: 'angles', enonce: 'Le demi-tour conserve les mesures d’angles.', statut: 'admise', commune: true },
    { id: 'alignement', enonce: 'Le demi-tour conserve l’alignement des points.', statut: 'admise', commune: true },
    { id: 'aires', enonce: 'Le demi-tour conserve les aires.', statut: 'admise', commune: true },
    { id: 'droite_parallele', enonce: 'Par un demi-tour, l’image d’une droite est une droite qui lui est parallèle.', statut: 'démontrée', commune: false },
    { id: 'centre_fixe', enonce: 'Le centre du demi-tour est son propre image.', statut: 'démontrée', commune: false },
    { id: 'involutive', enonce: 'Appliqué deux fois, le demi-tour redonne la figure de départ.', statut: 'démontrée', commune: false },
];
const propriete = (id) => PROPRIETES.find((p) => p.id === id);
/** L'énoncé de la propriété ET son statut, construits depuis la table (vigilance 1 et 2). */
const rappelPropriete = (id) => {
    const p = propriete(id);
    return `Propriété ${p.statut} : ${p.enonce}`;
};

// ═══════════════════════════════════════════════════════════
// Outillage de figure : tout est calculé, rien n'est mesuré
// ═══════════════════════════════════════════════════════════

// Pixels par unite de figure. A 17, les triangles etaient petits DEVANT les
// libelles, et le moindre decalage d'etiquette venait toucher un trait : la
// collision venait de la taille du dessin, pas seulement du placement (retour prof
// 2026-09-26). A 26, le dessin occupe la place, et les libelles tiennent a cote de
// ce qu'ils nomment sans avoir a fuir.
const PX = 26;
const NOIR = '#1f2937';
const BLEU = '#1E3A8A';
const ORANGE = '#b45309';
const GRIS = '#94a3b8';
const R1 = (v) => Number(v).toFixed(1);
/** Dixièmes de centimètre → écriture française exacte (« 5,4 », « 6 »). */
const frD = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);

const sym = (p, o) => [2 * o[0] - p[0], 2 * o[1] - p[1]];
const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const aireTri = (t) => Math.abs((t[1][0] - t[0][0]) * (t[2][1] - t[0][1]) - (t[1][1] - t[0][1]) * (t[2][0] - t[0][0])) / 2;
/**
 * Un point est-il DANS le triangle ? Deux convexes symétriques par rapport à O
 * sont disjoints si et seulement si O n'appartient pas au convexe : ce test
 * garantit donc à la fois l'exclusion 2 et l'exclusion 4.
 */
const dansTriangle = (p, t) => {
    const signe = (a, b, c) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
    const s1 = signe(t[0], t[1], p), s2 = signe(t[1], t[2], p), s3 = signe(t[2], t[0], p);
    return !((s1 < 0 || s2 < 0 || s3 < 0) && (s1 > 0 || s2 > 0 || s3 > 0));
};

/** Distance d'un point au segment [S, U], en coordonnées écran. */
const distTrait = (P, S, U) => {
    const vx = U[0] - S[0], vy = U[1] - S[1];
    const L2 = vx * vx + vy * vy;
    const t = L2 ? Math.max(0, Math.min(1, ((P[0] - S[0]) * vx + (P[1] - S[1]) * vy) / L2)) : 0;
    return Math.hypot(P[0] - (S[0] + t * vx), P[1] - (S[1] + t * vy));
};

/**
 * Pose le nom d'un sommet autour de (X, Y).
 *
 * Le nom était posé à 13 px dans la direction « vers l'extérieur » calculée
 * depuis le centre de TOUTE la planche — or la planche contient deux triangles
 * et le centre O, si bien que cette direction longeait souvent un côté du
 * triangle auquel le sommet appartient. Mesure : 14,7 % des noms tombaient à
 * 3 px ou moins d'un trait, le pire à 0,0 px (retour prof 2026-09-26).
 *
 * On essaie seize directions et trois éloignements, et on garde la pose la plus
 * dégagée de tous les traits ET des noms déjà posés, en sondant les deux bouts
 * du texte et non son seul centre. `poses` s'enrichit au fur et à mesure.
 */
const TOUTES_DIRECTIONS = [...Array(32)].map((_, i) => (i * Math.PI) / 16);

// Le nom du centre est le plus contraint : la croix, et les pointilles qui passent
// tous par O. Un quatrieme eloignement lui laisse une issue.
// Le nom du centre est le plus contraint de tous : la croix, plus TOUS les
// pointilles, qui passent par O.
const ECARTS_NOM = [13, 17, 22, 27];

const DEGAGEMENT_VOULU = 4;
/** Une LONGUEUR ne doit toucher aucun trait : on lui exige une vraie marge. */
const DEGAGEMENT_LONGUEUR = 8;
/** Un angle, lui, peut fraler un trait : il doit surtout rester dans son secteur. */
const DEGAGEMENT_ANGLE = 2;
/** Demi-hauteur d'un libelle de corps 12-13 : ce qui le fait chevaucher son voisin. */
const DEMI_HAUTEUR = 5.5;       // px : en dessous, le libelle touche presque le trait

const placerNom = (X, Y, txt, fs, traits, poses, dirs = TOUTES_DIRECTIONS, ecarts = ECARTS_NOM,
    voulu = DEGAGEMENT_VOULU, filtre = null, ancres = null) => {
    const demi = (String(txt).length * fs * 0.62) / 2;
    let best = null;        // le plus degage parmi ceux qui passent le filtre
    let proche = null;      // le plus PRES qui atteigne deja le degagement voulu
    let secours = null;     // si AUCUN candidat ne passe le filtre, on prend le plus degage
    // Une longueur peut aussi GLISSER le long de son cote : se contenter du milieu
    // ne laissait qu'une seule perpendiculaire, souvent barree par un pointille.
    (ancres || [[X, Y]]).forEach(([ax, ay]) => ecarts.forEach((d) => {
        dirs.forEach((a) => {
            const C = [ax + d * Math.cos(a), ay + d * Math.sin(a)];
            // Un libelle est une BOITE, pas une ligne : on le sonde sur ses deux bouts
            // ET sur son haut et son bas. Le sonder sur sa seule ligne mediane laissait
            // deux libelles se chevaucher verticalement (« 28 deg » et « B » a 0 px).
            const sondes = [];
            [-DEMI_HAUTEUR, 0, DEMI_HAUTEUR].forEach((dy) => {
                [-demi, 0, demi].forEach((dx) => sondes.push([C[0] + dx, C[1] + dy]));
            });
            const s = Math.min(...sondes.flatMap((S) => [...traits, ...poses].map((g) => distTrait(S, g[0], g[1]))));
            if (!secours || s > secours.s + 0.01) secours = { s, C };
            if (filtre && !filtre(C)) return;
            if (!best || s > best.s + 0.01) best = { s, C };
            if (s >= voulu && !proche) proche = { s, C };
        });
    }));
    // On ne prend PAS le plus degage : on prend le plus proche qui soit assez degage.
    // Chercher le maximum de degagement envoyait « 31 deg » a 39 px de son arc, loin
    // de l'angle qu'il mesure (retour prof 2026-09-26). Un libelle doit d'abord
    // rester lisible AVEC l'objet qu'il nomme ; il ne s'eloigne que s'il y est force.
    const choix = proche || best || secours;
    [-DEMI_HAUTEUR, 0, DEMI_HAUTEUR].forEach((dy) => {
        poses.push([[choix.C[0] - demi, choix.C[1] + dy], [choix.C[0] + demi, choix.C[1] + dy]]);
    });
    return choix.C;
};

/**
 * Dessin d'une planche. Le cadrage est calculé sur TOUS les objets tracés —
 * centre et segments vers les images compris (vigilance 9).
 * Objets : poly, seg, pt, centre, cote (longueur portée sur un côté), arc
 * (mesure d'angle au sommet), milieu (double marque au milieu d'un segment).
 *
 * Deux passes : on trace d'abord les traits en relevant leur position à l'écran,
 * puis on pose les noms de sommets là où ils ne tombent sur rien.
 */
const svgFig = (objets) => {
    const tous = [];
    objets.forEach((o) => {
        if (o.t === 'poly') tous.push(...o.pts);
        else if (o.t === 'seg' || o.t === 'cote' || o.t === 'milieu') tous.push(o.a, o.b);
        else if (o.t === 'arc') tous.push(o.s, o.p, o.q);
        else tous.push(o.p);
    });
    const xs = tous.map((p) => p[0]), ys = tous.map((p) => p[1]);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    // La marge doit loger le libelle pose au plus loin : l'ecartement maximal de
    // placerNom, plus la demi-largeur du texte et sa demi-hauteur. Elle se calcule
    // sur les libelles REELLEMENT portes, pour ne pas retrecir le dessin en vain.
    const demiDe = (t, fs) => (String(t).length * fs * 0.62) / 2;
    const demiNom = Math.max(6, ...objets.filter((o) => o.label).map((o) => demiDe(o.label, 13)));
    const demiMes = Math.max(6, ...objets.filter((o) => o.t === 'cote' || o.t === 'arc').map((o) => demiDe(o.texte, 12)));
    const pad = Math.ceil(Math.max(27 + demiNom, 32 + demiMes) + 6);
    const W = Math.round((maxX - minX) * PX + 2 * pad), H = Math.round((maxY - minY) * PX + 2 * pad);
    const px = (p) => pad + (p[0] - minX) * PX;
    const py = (p) => pad + (maxY - p[1]) * PX;
    const centreFig = [(minX + maxX) / 2, (minY + maxY) / 2];
    const traits = [];       // tous les traits traces, en coordonnees ecran
    // Le triangle de DEPART a l'ecran : les longueurs se posent dehors, les angles
    // dedans (arbitrage prof 2026-09-26).
    const dep = (objets.find((o) => o.t === 'poly' && !o.dash) || {}).pts;
    const triEcr = dep ? dep.map((q) => [px(q), py(q)]) : null;
    const dedansTri = (C) => {
        if (!triEcr) return true;
        const sg = (a2, b2, c2) => (b2[0] - a2[0]) * (c2[1] - a2[1]) - (b2[1] - a2[1]) * (c2[0] - a2[0]);
        const v = [sg(triEcr[0], triEcr[1], C), sg(triEcr[1], triEcr[2], C), sg(triEcr[2], triEcr[0], C)];
        return v.every((z) => z >= 0) || v.every((z) => z <= 0);
    };
    const noms = [];         // les noms de sommets, poses en seconde passe
    const mesures = [];      // longueurs et angles portes, poses eux aussi apres coup
    const out = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`];
    out.push(`<rect x="0" y="0" width="${W}" height="${H}" fill="white"/>`);
    objets.forEach((o) => {
        if (o.t === 'poly') {
            const e = o.pts.map((p) => [px(p), py(p)]);
            e.forEach((P, k) => traits.push([P, e[(k + 1) % e.length]]));
            const d = o.pts.map((p) => `${R1(px(p))},${R1(py(p))}`).join(' ');
            out.push(`<polygon points="${d}" fill="none" stroke="${o.couleur}" stroke-width="2"${o.dash ? ' stroke-dasharray="6 4"' : ''}/>`);
        } else if (o.t === 'seg') {
            traits.push([[px(o.a), py(o.a)], [px(o.b), py(o.b)]]);
            out.push(`<line x1="${R1(px(o.a))}" y1="${R1(py(o.a))}" x2="${R1(px(o.b))}" y2="${R1(py(o.b))}" stroke="${o.couleur}" stroke-width="${o.ep || 2}"${o.dash ? ' stroke-dasharray="6 4"' : ''}/>`);
        } else if (o.t === 'milieu') {
            const m = [(o.a[0] + o.b[0]) / 2, (o.a[1] + o.b[1]) / 2];
            const ux = (o.b[0] - o.a[0]) / dist(o.a, o.b), uy = (o.b[1] - o.a[1]) / dist(o.a, o.b);
            [-0.09, 0.09].forEach((k) => {
                const c = [m[0] + k * ux, m[1] + k * uy];
                out.push(`<line x1="${R1(px(c) - uy * 7)}" y1="${R1(py(c) - ux * 7)}" x2="${R1(px(c) + uy * 7)}" y2="${R1(py(c) + ux * 7)}" stroke="${ORANGE}" stroke-width="2"/>`);
            });
        } else if (o.t === 'cote') {
            const m = [(o.a[0] + o.b[0]) / 2, (o.a[1] + o.b[1]) / 2];
            // Arbitrage prof 2026-09-26 : les LONGUEURS se posent a l'exterieur de la
            // figure, et leur ecriture ne doit toucher aucun trait. On sort donc par la
            // normale au cote, du cote oppose au troisieme sommet, avec un cone etroit
            // pour rester en face du cote qu'on mesure, et un degagement exige eleve.
            const sortie = Math.atan2(py(m) - py(o.oppose), px(m) - px(o.oppose));
            const le = (t) => [o.a[0] + (o.b[0] - o.a[0]) * t, o.a[1] + (o.b[1] - o.a[1]) * t];
            mesures.push({
                X: px(m), Y: py(m), texte: o.texte, couleur: BLEU,
                // La mesure doit rester CONTRE le cote qu'elle designe : on part du
                // milieu, on sort perpendiculairement, et on ne s'ecarte qu'a peine.
                // Les ancres decalees et le cone large ne servent que de repli.
                ancres: [0.5, 0.42, 0.58, 0.32, 0.68].map((t) => [px(le(t)), py(le(t))]),
                dirs: [0, -0.25, 0.25, -0.5, 0.5].map((e) => sortie + e),
                ecarts: [14, 18, 22, 27, 32], voulu: DEGAGEMENT_LONGUEUR,
                filtre: (C) => !dedansTri(C),
            });
        } else if (o.t === 'arc') {
            const a1 = Math.atan2(o.p[1] - o.s[1], o.p[0] - o.s[0]);
            const a2 = Math.atan2(o.q[1] - o.s[1], o.q[0] - o.s[0]);
            const pts = [];
            for (let k = 0; k <= 12; k++) {
                let d = a2 - a1;
                while (d > Math.PI) d -= 2 * Math.PI;
                while (d < -Math.PI) d += 2 * Math.PI;
                const a = a1 + (d * k) / 12;
                pts.push(`${R1(px([o.s[0] + 0.55 * Math.cos(a), o.s[1] + 0.55 * Math.sin(a)]))},${R1(py([o.s[0] + 0.55 * Math.cos(a), o.s[1] + 0.55 * Math.sin(a)]))}`);
            }
            out.push(`<polyline points="${pts.join(' ')}" fill="none" stroke="#15803d" stroke-width="1.6"/>`);
            const ecr = pts.map((c) => c.split(',').map(Number));
            const sien = [];
            for (let k = 0; k < ecr.length - 1; k++) { const g = [ecr[k], ecr[k + 1]]; traits.push(g); sien.push(g); }
            // La mesure d'angle doit rester DANS l'angle : ses directions candidates
            // sont bornees a un cone autour de la bissectrice, sinon elle irait
            // designer un autre secteur.
            const bx = (o.p[0] + o.q[0]) / 2 - o.s[0], by = (o.p[1] + o.q[1]) / 2 - o.s[1];
            const bis = Math.atan2(-by, bx);          // y ecran vers le bas
            mesures.push({
                X: px(o.s), Y: py(o.s), texte: o.texte, couleur: '#15803d',
                // Son propre arc n'est pas un obstacle : la mesure est FAITE pour se
                // poser juste au-dela. Le compter comme un trait a eviter poussait le
                // libelle loin de l'angle qu'il mesure.
                sien,
                // La bissectrice pointe vers le milieu des deux autres sommets : elle
                // mene donc DEDANS, ce que le prof demande pour les angles. Cone etroit
                // pour ne pas sortir du secteur.
                dirs: [0, -0.3, 0.3, -0.55, 0.55].map((e) => bis + e),
                voulu: DEGAGEMENT_ANGLE,
                filtre: dedansTri,
                // Un angle etroit (la spec descend a 25 degres) ne peut pas loger un
                // libelle de 30 px pres de son sommet : il faut pouvoir s'ecarter un peu.
                ecarts: [21, 25, 29],
            });
        } else if (o.t === 'centre') {
            traits.push([[px(o.p) - 6, py(o.p)], [px(o.p) + 6, py(o.p)]]);
            traits.push([[px(o.p), py(o.p) - 6], [px(o.p), py(o.p) + 6]]);
            out.push(`<line x1="${R1(px(o.p) - 6)}" y1="${R1(py(o.p))}" x2="${R1(px(o.p) + 6)}" y2="${R1(py(o.p))}" stroke="#dc2626" stroke-width="2"/>`);
            out.push(`<line x1="${R1(px(o.p))}" y1="${R1(py(o.p) - 6)}" x2="${R1(px(o.p))}" y2="${R1(py(o.p) + 6)}" stroke="#dc2626" stroke-width="2"/>`);
            noms.push({ p: o.p, label: o.label, couleur: '#dc2626' });
        } else {
            out.push(`<circle cx="${R1(px(o.p))}" cy="${R1(py(o.p))}" r="3.4" fill="${o.couleur || NOIR}"/>`);
            if (o.label) noms.push({ p: o.p, label: o.label, couleur: o.couleur || NOIR });
        }
    });
    // Seconde passe, une fois tous les traits connus. Les MESURES d'abord : elles
    // sont attachees a un cote ou a un angle precis, donc les moins libres. Les noms
    // de sommets ensuite, qui les evitent a leur tour (« 4,7 cm » et « 49 deg » se
    // chevauchaient pres du meme sommet, retour prof 2026-09-26).
    const poses = [];
    // La mesure d'angle est bornee a un cone ; la longueur peut aller dans toutes
    // les directions. On pose donc la plus contrainte en premier, sinon la seconde
    // occupe sa seule issue et les deux se chevauchent.
    mesures.sort((x, y) => (x.dirs ? 0 : 1) - (y.dirs ? 0 : 1)).forEach((ms) => {
        const aEviter = ms.sien ? traits.filter((g) => !ms.sien.includes(g)) : traits;
        const C = placerNom(ms.X, ms.Y, ms.texte, 12, aEviter, poses, ms.dirs, ms.ecarts, ms.voulu, ms.filtre, ms.ancres);
        out.push(`<text x="${R1(C[0])}" y="${R1(C[1] + 4)}" text-anchor="middle" font-size="12" font-weight="700" fill="${ms.couleur}">${ms.texte}</text>`);
    });
    noms.forEach((nm) => {
        const C = placerNom(px(nm.p), py(nm.p), nm.label, 13, traits, poses);
        out.push(`<text x="${R1(C[0])}" y="${R1(C[1] + 4)}" text-anchor="middle" font-size="13" font-weight="700" fill="${nm.couleur}">${nm.label}</text>`);
    });
    out.push('</svg>');
    return out.join('');
};

const figEnonce = (svg, alt) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'medium', alt } }],
});

// ═══════════════════════════════════════════════════════════
// Tirage de la planche « figure cotée + image sans cote »
// ═══════════════════════════════════════════════════════════

const LETTRES = 'ABCDEFGHJKLMNPRSTUVZ'.split('');

/**
 * Triangle dont les trois angles sont entiers de 25 à 130 degrés et les trois
 * côtés décimaux de 3 à 8 cm, d'aire au moins 8. Orientation libre sur un tour.
 * Boucle bornée et repli déterministe (vigilance 8).
 */
const tirerTriangle = () => {
    for (let essai = 0; essai < 400; essai++) {
        const A = randInt(25, 130), B = randInt(25, 130);
        const C = 180 - A - B;
        if (C < 25 || C > 130) continue;
        const aDix = randInt(30, 80);                       // côté opposé à A, en dixièmes
        const a = aDix / 10;
        const k = a / Math.sin((A * Math.PI) / 180);
        const b = k * Math.sin((B * Math.PI) / 180);
        const c = k * Math.sin((C * Math.PI) / 180);
        if (b < 3 || b > 8 || c < 3 || c > 8) continue;
        const bDix = Math.round(b * 10), cDix = Math.round(c * 10);
        const theta = (randInt(0, 359) * Math.PI) / 180;    // orientation libre
        // sommet A à l'origine, B à distance c dans la direction theta
        const S = [
            [0, 0],
            [c * Math.cos(theta), c * Math.sin(theta)],
            [b * Math.cos(theta + (A * Math.PI) / 180), b * Math.sin(theta + (A * Math.PI) / 180)],
        ];
        if (aireTri(S) < 8) continue;
        return {
            sommets: S, angles: [A, B, C], cotesDix: [aDix, bDix, cDix],
            // côté opposé au sommet i : [S1S2], [S2S0], [S0S1]
        };
    }
    const S = [[0, 0], [6, 0], [2, 4]];
    return { sommets: S, angles: [63, 45, 72], cotesDix: [45, 45, 60] };
};

/**
 * Planche complète : triangle coté, centre hors de la figure, image sans
 * aucune cote, segments sommet → image avec le milieu codé.
 */
const tirerPlanche = () => {
    const tri = tirerTriangle();
    const S = tri.sommets;
    const g = [(S[0][0] + S[1][0] + S[2][0]) / 3, (S[0][1] + S[1][1] + S[2][1]) / 3];
    let O = null;
    for (let essai = 0; essai < 300 && !O; essai++) {
        const ang = (randInt(0, 359) * Math.PI) / 180;
        const r = 3 + randInt(0, 30) / 10;
        const cand = [g[0] + r * Math.cos(ang), g[1] + r * Math.sin(ang)];
        // exclusion 2 : hors de la figure, et à deux unités au moins de chaque sommet
        if (!dansTriangle(cand, S) && S.every((s) => dist(s, cand) >= 2)) O = cand;
    }
    if (!O) O = [g[0] + 5, g[1] + 5];
    const image = S.map((p) => sym(p, O));
    // exclusion 6 : les noms de l'image ne se déduisent pas alphabétiquement de
    // ceux du départ — ni la lettre suivante, ni un décalage constant.
    let noms = LETTRES.slice(0, 3), nomsImage = LETTRES.slice(3, 6);
    for (let essai = 0; essai < 60; essai++) {
        const tirage = shuffle(LETTRES).slice(0, 6);
        const a = tirage.slice(0, 3), b = tirage.slice(3, 6);
        const suivante = a.some((n, k) => b[k].charCodeAt(0) === n.charCodeAt(0) + 1);
        const ecarts = new Set(a.map((n, k) => b[k].charCodeAt(0) - n.charCodeAt(0)));
        if (!suivante && ecarts.size > 1) { noms = a; nomsImage = b; break; }
    }
    return { tri, S, O, image, noms, nomsImage };
};

/** Objets de dessin : la figure de départ est cotée, l'image ne l'est JAMAIS (exclusion 1). */
const objetsPlanche = (pl, { cotes = [], angles = [], nommerImage = true } = {}) => {
    const o = [
        { t: 'poly', pts: pl.S, couleur: BLEU },
        { t: 'poly', pts: pl.image, couleur: ORANGE, dash: true },
    ];
    pl.S.forEach((p, i) => {
        o.push({ t: 'seg', a: p, b: pl.image[i], couleur: GRIS, ep: 1.2, dash: true });
        o.push({ t: 'milieu', a: p, b: pl.image[i] });
    });
    o.push({ t: 'centre', p: pl.O, label: 'O' });
    pl.S.forEach((p, i) => o.push({ t: 'pt', p, label: pl.noms[i], couleur: BLEU }));
    pl.image.forEach((p, i) => o.push({ t: 'pt', p, label: nommerImage ? pl.nomsImage[i] : '', couleur: ORANGE }));
    // cotes : uniquement sur la figure de DÉPART
    cotes.forEach((i) => {
        const a = pl.S[(i + 1) % 3], b = pl.S[(i + 2) % 3];
        // `oppose` est le troisieme sommet : il dit de quel cote est le DEDANS du
        // triangle, donc dans quelle direction sortir pour poser la longueur dehors.
        o.push({ t: 'cote', a, b, oppose: pl.S[i], texte: `${frD(pl.tri.cotesDix[i])} cm` });
    });
    angles.forEach((i) => {
        o.push({ t: 'arc', s: pl.S[i], p: pl.S[(i + 1) % 3], q: pl.S[(i + 2) % 3], texte: `${pl.tri.angles[i]}°` });
    });
    return o;
};

const NOM_COTE = (pl, i) => `[${pl.noms[(i + 1) % 3]}${pl.noms[(i + 2) % 3]}]`;
const NOM_COTE_IMAGE = (pl, i) => `[${pl.nomsImage[(i + 1) % 3]}${pl.nomsImage[(i + 2) % 3]}]`;

// ═══════════════════════════════════════════════════════════
// Carte « Déduire par les propriétés du demi-tour »
// ═══════════════════════════════════════════════════════════

const genTransportSaisie = () => {
    const pl = tirerPlanche();
    const quoi = randomChoice(['longueur', 'angle', 'aire']);
    const nommerImage = randomChoice([true, true, false]);
    let bonne, acceptees, question, prop, objets;
    if (quoi === 'longueur') {
        const i = randInt(0, 2);
        prop = 'longueurs';
        objets = objetsPlanche(pl, { cotes: [i], nommerImage });
        bonne = `${frD(pl.tri.cotesDix[i])} cm`;
        acceptees = [bonne, `${frD(pl.tri.cotesDix[i])}cm`];
        question = `Quelle est la longueur du côté ${nommerImage ? NOM_COTE_IMAGE(pl, i) : 'image'} `
            + `de la figure orange, image de ${NOM_COTE(pl, i)} ?`;
    } else if (quoi === 'angle') {
        const i = randInt(0, 2);
        prop = 'angles';
        objets = objetsPlanche(pl, { angles: [i], nommerImage });
        bonne = `${pl.tri.angles[i]}°`;
        acceptees = [bonne, `${pl.tri.angles[i]} °`, `${pl.tri.angles[i]} degrés`];
        question = `Quelle est la mesure de l'angle de la figure orange, image de l'angle de sommet ${pl.noms[i]} ?`;
    } else {
        prop = 'aires';
        const aireDix = Math.round(aireTri(pl.S) * 10);
        objets = objetsPlanche(pl, { nommerImage });
        bonne = `${frD(aireDix)} cm²`;
        acceptees = [bonne, `${frD(aireDix)}cm²`, `${frD(aireDix)} cm2`, `${frD(aireDix)} cm^2`];
        question = `La figure bleue a une aire de <b>${frD(aireDix)} cm²</b>. `
            + 'Quelle est l\'aire de la figure orange ?';
    }
    const texte = '<p>La figure orange est l\'image de la figure bleue par le demi-tour de centre O. '
        + '<b>Seules les mesures de la figure de départ sont indiquées.</b></p>'
        + `<p><b>${question}</b></p>`
        + `<p><i>${rappelPropriete(prop)} Une unité est attendue dans la réponse.</i></p>`;
    const payload = makeSaisie(bonne, texte, {
        ...opts(L_PROP), suffixe: '',
        comparaison: 'expression', reponses_acceptees: acceptees,
        elements_additionnels: figEnonce(svgFig(objets), 'Figure cotée et son image par le demi-tour, sans cote'),
    });
    payload._meta.params = {
        niveau: quoi === 'longueur' ? 'n1' : (quoi === 'angle' ? 'n2' : 'n6'),
        propriete: prop, statut: propriete(prop).statut, nbProprietes: 1, nommerImage,
        angles: pl.tri.angles, cotesDix: pl.tri.cotesDix, centre: pl.O, sommets: pl.S,
        erreursVisees: ['mesure_sur_le_dessin', 'unite_manquante'],
    };
    return payload;
};

/** Niveau 3 : une longueur ET un angle, à deux endroits distincts de l'image. */
const genTransportDouble = () => {
    const pl = tirerPlanche();
    const i = randInt(0, 2);
    const j = (i + randInt(1, 2)) % 3;
    const nommerImage = randomChoice([true, true, false]);
    const phrase = `Longueur du côté image de ${NOM_COTE(pl, i)} : {{${frD(pl.tri.cotesDix[i])}}} cm\n`
        + `Mesure de l'angle image de celui de sommet ${pl.noms[j]} : {{${pl.tri.angles[j]}}}°`;
    const texte = '<p>La figure orange est l\'image de la figure bleue par le demi-tour de centre O. '
        + '<b>Seules les mesures de la figure de départ sont indiquées.</b> Complète.</p>'
        + `<p><i>${rappelPropriete('longueurs')} ${rappelPropriete('angles')}</i></p>`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_PROP), mode: 'saisie', strict: false,
        elements_additionnels: figEnonce(svgFig(objetsPlanche(pl, { cotes: [i], angles: [j], nommerImage })),
            'Figure cotée et son image par le demi-tour, sans cote'),
    });
    payload._meta.params = {
        niveau: 'n3', propriete: 'longueurs', statut: propriete('longueurs').statut,
        nbProprietes: 2, nommerImage,
        angles: pl.tri.angles, cotesDix: pl.tri.cotesDix, centre: pl.O, sommets: pl.S,
        erreursVisees: ['seulement_les_longueurs', 'mauvaise_correspondance'],
    };
    return payload;
};

/** Désigner la propriété invoquée, dans la liste des sept (type_reponse, forme 2). */
const genProprietesQcm = () => {
    const pl = tirerPlanche();
    const quoi = randomChoice(['longueur', 'angle', 'aire']);
    const prop = { longueur: 'longueurs', angle: 'angles', aire: 'aires' }[quoi];
    const i = randInt(0, 2);
    const objets = objetsPlanche(pl, quoi === 'longueur' ? { cotes: [i] } : (quoi === 'angle' ? { angles: [i] } : {}));
    const question = {
        longueur: `Quelle propriété permet de connaître la longueur du côté image de ${NOM_COTE(pl, i)} ?`,
        angle: `Quelle propriété permet de connaître la mesure de l'angle image de celui de sommet ${pl.noms[i]} ?`,
        aire: 'Quelle propriété permet de connaître l\'aire de la figure orange ?',
    }[quoi];
    const correct = propriete(prop).enonce;
    const distracteurs = shuffle(PROPRIETES.filter((p) => p.id !== prop)).slice(0, 3)
        .map((p) => ({ code: p.commune ? 'seulement_les_longueurs' : 'conservation_mal_attribuee', valeur: p.enonce }));
    const texte = '<p>La figure orange est l\'image de la figure bleue par le demi-tour de centre O.</p>'
        + `<p><b>${question}</b></p>`;
    const payload = makeQcm([correct, ...distracteurs.map((d) => d.valeur)], correct, texte, {
        ...opts(L_PROP), elements_additionnels: figEnonce(svgFig(objets), 'Figure cotée et son image par le demi-tour'),
    });
    payload._meta.params = {
        niveau: quoi === 'longueur' ? 'n1' : (quoi === 'angle' ? 'n2' : 'n6'),
        // Le statut n'est pas affiché ici : la question EST de trouver la
        // propriété, l'annoncer donnerait la réponse (voir le rapport).
        propriete: prop, statut: null, nbProprietes: 1, nommerImage: true,
        angles: pl.tri.angles, cotesDix: pl.tri.cotesDix, centre: pl.O, sommets: pl.S,
        distracteurs, erreursVisees: [],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte « Conclure par une propriété »
// ═══════════════════════════════════════════════════════════

/**
 * Niveau 4 — alignement. Dans le cas NON aligné, la conclusion ne vient jamais
 * de la conservation de l'alignement (qui ne dit rien dans ce sens) mais du
 * caractère involutif du demi-tour (vigilance 3).
 */
const genAlignementQcm = () => {
    const alignes = randInt(1, 3) !== 1;                  // 2 cas sur 3 concluent (proportion 0,5 à 0,75)
    const ecart = randInt(3, 6);                          // degrés, indiscernables à l'œil
    const lettres = shuffle(LETTRES).slice(0, 4);
    const [n1, n2, n3] = lettres;
    const theta = (randInt(0, 359) * Math.PI) / 180;
    const P = [0, 0];
    const Q = [3.2 * Math.cos(theta), 3.2 * Math.sin(theta)];
    const a3 = theta + (alignes ? 0 : (ecart * Math.PI) / 180);
    const R = [Q[0] + 3.4 * Math.cos(a3), Q[1] + 3.4 * Math.sin(a3)];
    const O = [Q[0] + 4.5 * Math.cos(theta + Math.PI / 2), Q[1] + 4.5 * Math.sin(theta + Math.PI / 2)];
    const img = [P, Q, R].map((p) => sym(p, O));
    const objets = [
        { t: 'seg', a: P, b: R, couleur: BLEU, ep: 1.2 },
        { t: 'seg', a: img[0], b: img[2], couleur: ORANGE, ep: 1.2, dash: true },
        ...[P, Q, R].map((p, i) => ({ t: 'pt', p, label: [n1, n2, n3][i], couleur: BLEU })),
        ...img.map((p) => ({ t: 'pt', p, label: '', couleur: ORANGE })),
        ...[P, Q, R].map((p, i) => ({ t: 'seg', a: p, b: img[i], couleur: GRIS, ep: 1, dash: true })),
        { t: 'centre', p: O, label: 'O' },
    ];
    const correct = alignes
        ? `Oui : le demi-tour conserve l’alignement, donc les images de ${n1}, ${n2} et ${n3} sont alignées.`
        : `Non : si les images étaient alignées, en leur appliquant de nouveau le demi-tour on retrouverait ${n1}, ${n2} et ${n3} alignés, ce qui est faux.`;
    const distracteurs = alignes
        ? [
            { code: 'demi_tour_change_les_mesures', valeur: 'Non : le demi-tour retourne la figure, l’alignement est perdu.' },
            { code: 'mesure_sur_le_dessin', valeur: 'On ne peut pas savoir sans mesurer sur le dessin.' },
            { code: 'parallele_pour_alignement', valeur: 'Oui : l’image d’une droite est une droite parallèle, donc les images sont alignées avec la droite de départ.' },
        ]
        : [
            { code: 'reciproque_mal_utilisee', valeur: `Non : le demi-tour conserve l’alignement, donc les images de ${n1}, ${n2} et ${n3} ne sont pas alignées.` },
            { code: 'mesure_sur_le_dessin', valeur: 'On ne peut pas savoir sans mesurer sur le dessin.' },
            { code: 'demi_tour_change_les_mesures', valeur: 'Oui : le demi-tour aligne toujours les images de trois points.' },
        ];
    const texte = `<p>Les points ${n1}, ${n2} et ${n3} ${alignes ? 'sont alignés' : 'ne sont PAS alignés'} `
        + '(le dessin ne permet pas d\'en décider : fie-toi à l\'énoncé). '
        + 'La figure orange montre leurs images par le demi-tour de centre O.</p>'
        + '<p><b>Les images sont-elles alignées ? Choisis la réponse justifiée.</b></p>'
        + `<p><i>${rappelPropriete(alignes ? 'alignement' : 'involutive')}</i></p>`;
    const payload = makeQcm([correct, ...distracteurs.map((d) => d.valeur)], correct, texte, {
        ...opts(L_CONCLURE), elements_additionnels: figEnonce(svgFig(objets), 'Trois points et leurs images par le demi-tour'),
    });
    payload._meta.params = {
        niveau: 'n4', propriete: alignes ? 'alignement' : 'involutive',
        statut: propriete(alignes ? 'alignement' : 'involutive').statut,
        nbProprietes: 1, conclut: alignes, ecartAlignement: alignes ? null : ecart,
        centre: O, sommets: [P, Q, R], distracteurs,
        erreursVisees: ['reciproque_mal_utilisee'],
    };
    return payload;
};

/** Niveau 5 — l'image d'une droite est une droite qui lui est parallèle. */
const genParalleleQcm = () => {
    const lettres = shuffle(LETTRES).slice(0, 2);
    const theta = (randInt(0, 359) * Math.PI) / 180;
    const P = [0, 0];
    const Q = [4.5 * Math.cos(theta), 4.5 * Math.sin(theta)];
    const O = [Q[0] / 2 + 3.6 * Math.cos(theta + Math.PI / 2), Q[1] / 2 + 3.6 * Math.sin(theta + Math.PI / 2)];
    const img = [P, Q].map((p) => sym(p, O));
    const objets = [
        { t: 'seg', a: P, b: Q, couleur: BLEU },
        { t: 'seg', a: img[0], b: img[1], couleur: ORANGE, dash: true },
        ...[P, Q].map((p, i) => ({ t: 'pt', p, label: lettres[i], couleur: BLEU })),
        ...[P, Q].map((p, i) => ({ t: 'seg', a: p, b: img[i], couleur: GRIS, ep: 1, dash: true })),
        ...[P, Q].map((p, i) => ({ t: 'milieu', a: p, b: img[i] })),
        { t: 'centre', p: O, label: 'O' },
    ];
    const correct = 'Elles sont parallèles : par un demi-tour, l’image d’une droite est une droite qui lui est parallèle.';
    const distracteurs = [
        { code: 'parallele_pour_alignement', valeur: 'Elles sont confondues : le demi-tour conserve l’alignement.' },
        { code: 'demi_tour_change_les_mesures', valeur: 'Elles sont perpendiculaires : le demi-tour fait faire un demi-tour à la droite.' },
        { code: 'mesure_sur_le_dessin', valeur: 'On ne peut pas savoir sans mesurer les angles sur le dessin.' },
    ];
    const texte = `<p>La droite (${lettres[0]}${lettres[1]}) est dessinée en bleu. `
        + 'Son image par le demi-tour de centre O est dessinée en orange.</p>'
        + '<p><b>Quelle est la position de ces deux droites l\'une par rapport à l\'autre ?</b></p>'
        + `<p><i>${rappelPropriete('droite_parallele')}</i></p>`;
    const payload = makeQcm([correct, ...distracteurs.map((d) => d.valeur)], correct, texte, {
        ...opts(L_CONCLURE), elements_additionnels: figEnonce(svgFig(objets), 'Une droite et son image par le demi-tour'),
    });
    payload._meta.params = {
        niveau: 'n5', propriete: 'droite_parallele', statut: propriete('droite_parallele').statut,
        nbProprietes: 1, conclut: true, centre: O, sommets: [P, Q], distracteurs,
        erreursVisees: ['parallele_pour_alignement'],
    };
    return payload;
};

/** Niveau 7 — le centre est son propre image, et le demi-tour est sa propre réciproque. */
const QUESTIONS_CENTRE = [
    {
        propriete: 'centre_fixe',
        question: 'Quelle est l’image du centre O par le demi-tour de centre O ?',
        correct: 'C’est le point O lui-même.',
        distracteurs: [
            { code: 'demi_tour_change_les_mesures', valeur: 'C’est un autre point, situé de l’autre côté de la figure.' },
            { code: 'conservation_mal_attribuee', valeur: 'Le centre n’a pas d’image : la transformation ne s’y applique pas.' },
            { code: 'mesure_sur_le_dessin', valeur: 'On ne peut pas le savoir sans mesurer sur le dessin.' },
        ],
    },
    {
        propriete: 'involutive',
        question: 'On applique deux fois de suite le demi-tour de centre O à une figure. Qu’obtient-on ?',
        correct: 'On retrouve la figure de départ.',
        distracteurs: [
            { code: 'demi_tour_change_les_mesures', valeur: 'On obtient une figure deux fois plus éloignée du centre.' },
            { code: 'conservation_mal_attribuee', valeur: 'On obtient l’image par une symétrie axiale.' },
            { code: 'reciproque_mal_utilisee', valeur: 'On obtient une figure de même forme, mais retournée une seconde fois.' },
        ],
    },
    {
        propriete: 'centre_fixe',
        question: 'Un point est sa propre image par le demi-tour de centre O. Que peut-on dire de ce point ?',
        correct: 'C’est le centre O.',
        distracteurs: [
            { code: 'conservation_mal_attribuee', valeur: 'C’est n’importe quel point de la figure.' },
            { code: 'demi_tour_change_les_mesures', valeur: 'Un tel point n’existe pas.' },
            { code: 'reciproque_mal_utilisee', valeur: 'C’est un point situé à égale distance des deux figures.' },
        ],
    },
];

const genCentreQcm = () => {
    const q = randomChoice(QUESTIONS_CENTRE);
    const pl = tirerPlanche();
    const texte = '<p>La figure orange est l’image de la figure bleue par le demi-tour de centre O.</p>'
        + `<p><b>${q.question}</b></p><p><i>${rappelPropriete(q.propriete)}</i></p>`;
    const payload = makeQcm([q.correct, ...q.distracteurs.map((d) => d.valeur)], q.correct, texte, {
        ...opts(L_CONCLURE),
        elements_additionnels: figEnonce(svgFig(objetsPlanche(pl, {})), 'Figure et son image par le demi-tour de centre O'),
    });
    payload._meta.params = {
        niveau: 'n7', propriete: q.propriete, statut: propriete(q.propriete).statut,
        nbProprietes: 1, conclut: true, distracteurs: q.distracteurs,
        angles: pl.tri.angles, cotesDix: pl.tri.cotesDix, centre: pl.O, sommets: pl.S,
        nommerImage: true, erreursVisees: ['conservation_mal_attribuee'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte « Définition, propriété, ou ni l'un ni l'autre ? »
// ═══════════════════════════════════════════════════════════

const DEFINITION_952 = 'Le demi-tour de centre O est la transformation qui laisse O fixe et qui, '
    + 'à tout autre point M, associe le point M\' tel que O soit le milieu du segment [MM\'].';
const HORS_SUJET = [
    { code: 'conservation_mal_attribuee', valeur: 'Le demi-tour agrandit les figures.' },
    { code: 'demi_tour_change_les_mesures', valeur: 'Le demi-tour change la mesure des angles.' },
    { code: 'conservation_mal_attribuee', valeur: 'Par un demi-tour, tous les points de la figure restent à leur place.' },
    { code: 'parallele_pour_alignement', valeur: 'Par un demi-tour, l’image d’une droite est une droite qui lui est perpendiculaire.' },
    { code: 'demi_tour_change_les_mesures', valeur: 'Le demi-tour transforme un segment en un segment plus court.' },
    { code: 'conservation_mal_attribuee', valeur: 'Le demi-tour multiplie les aires par deux.' },
    { code: 'parallele_pour_alignement', valeur: 'Par un demi-tour, trois points alignés ont trois images qui ne le sont plus.' },
    { code: 'conservation_mal_attribuee', valeur: 'Le centre du demi-tour est toujours un sommet de la figure.' },
    { code: 'reciproque_mal_utilisee', valeur: 'Appliqué deux fois, le demi-tour donne une figure retournée une seconde fois.' },
    { code: 'demi_tour_change_les_mesures', valeur: 'Le demi-tour change la longueur des côtés mais garde leur direction.' },
];
// Le prénom de l'élève qui écrit l'énoncé : les questions de statut ne sont
// jamais deux fois les mêmes, et le français reste celui d'une classe.
const PRENOMS = ['Léa', 'Tom', 'Inès', 'Noah', 'Jade', 'Sami', 'Lina', 'Enzo', 'Maya', 'Malo'];
const STATUTS = ['C’est la définition du demi-tour.', 'C’est une propriété du demi-tour.', 'C’est faux : cela ne vaut pas pour le demi-tour.'];

const genStatutQcm = () => {
    const genre = randomChoice(['definition', 'propriete', 'propriete', 'faux']);
    let enonce, correct, code;
    if (genre === 'definition') { enonce = DEFINITION_952; correct = STATUTS[0]; code = null; }
    else if (genre === 'propriete') { const p = randomChoice(PROPRIETES); enonce = p.enonce; correct = STATUTS[1]; code = null; }
    else { const f = randomChoice(HORS_SUJET); enonce = f.valeur; correct = STATUTS[2]; code = f.code; }
    const texte = `<p>${randomChoice(PRENOMS)} écrit dans son cahier : <b>« ${enonce} »</b></p>`
        + '<p><b>Cet énoncé est-il la définition du demi-tour, une propriété du demi-tour, ou ni l\'une ni l\'autre ?</b></p>'
        + '<p><i>Rappel : une définition permet de décider, pour un point quelconque, quelle est son image ; '
        + 'une propriété se déduit de la définition et ne suffit pas à la remplacer.</i></p>';
    const payload = makeQcm([...STATUTS], correct, texte, opts(L_STATUT));
    payload.contenu.choix = [...STATUTS];
    payload.contenu.bonne_reponse = 'ABC'[STATUTS.indexOf(correct)];
    payload._meta.params = {
        niveau: 'n8', genre, propriete: null, statut: null, nbProprietes: 1,
        distracteurs: code ? [{ code, valeur: enonce }] : [],
        erreursVisees: ['conservation_mal_attribuee'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte « Les propriétés du demi-tour (Vrai/Faux) »
// ═══════════════════════════════════════════════════════════

const genProprietesVf = () => {
    const vraies = shuffle(PROPRIETES).slice(0, 3).map((p) => p.enonce);
    const fausses = shuffle(HORS_SUJET).slice(0, 3).map((f) => f.valeur);
    const lignes = shuffle([...vraies.map((l) => [l, 0]), ...fausses.map((l) => [l, 1])]);
    const payload = makeVraiFaux(lignes.map((x) => x[0]), lignes.map((x) => x[1]),
        '<p><b>Le demi-tour est aussi appelé symétrie centrale.</b> Pour chaque affirmation, '
        + 'indique si elle est vraie ou fausse.</p>',
        opts(L_VF));
    payload._meta.params = {
        niveau: 'n8', propriete: null, statut: null, nbProprietes: 2,
        erreursVisees: ['seulement_les_longueurs', 'conservation_mal_attribuee'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    proprietes_texte: genTransportSaisie,
    proprietes_qcm_unique: genProprietesQcm,
    proprietes_double_texte_trou_saisie: genTransportDouble,
    conclure_alignement_qcm_unique: genAlignementQcm,
    conclure_parallele_qcm_unique: genParalleleQcm,
    conclure_centre_qcm_unique: genCentreQcm,
    statut_qcm_unique: genStatutQcm,
    vrai_faux_tableau_vf: genProprietesVf,
};

const FORMAT_LABELS = {
    texte: 'Saisie', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux',
    double_texte_trou_saisie: 'Saisie (longueur et angle)',
    alignement_qcm_unique: 'QCM alignement', parallele_qcm_unique: 'QCM droites',
    centre_qcm_unique: 'QCM centre et réciproque',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'proprietes', label: L_PROP, formatsList: buildFormats('proprietes') },
    { id: 'conclure', label: L_CONCLURE, formatsList: buildFormats('conclure') },
    { id: 'statut', label: L_STATUT, formatsList: buildFormats('statut') },
    { id: 'vrai_faux', label: L_VF, formatsList: buildFormats('vrai_faux') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { PROPRIETES, tirerTriangle, tirerPlanche, dansTriangle, aireTri, sym };
